import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { pdf } from "@react-pdf/renderer";
import InvoicePDF from "../InvoicePDF";
import { decryptField } from "../../utils/encryption";
import MuiAlert from "@mui/material/Alert";

export default function DownloadInvoicesDialog({
  open,
  onClose,
  invoices = [],
  prefCompanyName,
  prefCompanyAddress,
  prefCompanyNumber,
  prefBankDetails,
  logoBase64Cached,
}) {
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const handleDownload = async () => {
    setError(null);
    if (!startDate || !endDate) {
      setError("Please select both a start and end date.");
      return;
    }

    if (startDate.isAfter(endDate)) {
      setError("Start date cannot be after end date.");
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    try {
      // Filter invoices based on date range
      const filtered = invoices.filter((inv) => {
        const d = inv.invoiceDate?.toDate?.() || new Date(inv.invoiceDate);
        const invDate = dayjs(d);
        return (
          (invDate.isAfter(startDate, "day") || invDate.isSame(startDate, "day")) &&
          (invDate.isBefore(endDate, "day") || invDate.isSame(endDate, "day"))
        );
      });

      if (filtered.length === 0) {
        setError("No invoices found in the selected date range.");
        setIsProcessing(false);
        return;
      }

      const zip = new JSZip();

      // Process invoices one by one
      for (let i = 0; i < filtered.length; i++) {
        const inv = filtered[i];

        // Decrypt / prepare invoice data exactly like we do for singular viewed PDFs
        const decryptedInvoice = {
          ...inv,
          companyName: inv.companyName || prefCompanyName,
          companyAddress: decryptField(inv.companyAddress) || prefCompanyAddress,
          companyNumber: decryptField(inv.companyNumber) || prefCompanyNumber,
          clientName: inv.clientName, // Already decrypted at fetch level
          clientAddress: decryptField(inv.clientAddress),
          bankDetails: decryptField(inv.bankDetails) || prefBankDetails,
          notes: decryptField(inv.notes),
          logoUrl: logoBase64Cached,
        };

        // Render PDF to blob
        const blob = await pdf(<InvoicePDF invoice={decryptedInvoice} />).toBlob();

        // Safe filename replacing invalid chars
        const safeInvoiceNo = String(inv.invoiceNumber || "unknown").replace(/[^a-z0-9_-]/gi, '_');
        const safeClientName = String(inv.clientName || "Client").replace(/[^a-z0-9_-]/gi, '_');
        const fileName = `Invoice_${safeInvoiceNo}_${safeClientName}.pdf`;

        zip.file(fileName, blob);

        setProgress(Math.round(((i + 1) / filtered.length) * 100));
      }

      // Generate the zip folder
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipFileName = `Invoices_${startDate.format("YYMMDD")}_to_${endDate.format("YYMMDD")}.zip`;

      saveAs(zipBlob, zipFileName);

      // Reset and close on success
      setTimeout(() => {
        setIsProcessing(false);
        setProgress(0);
        onClose();
      }, 500);

    } catch (err) {
      console.error("Export zip error:", err);
      setError("An error occurred while generating the PDFs. Please checking your connection and try again.");
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onClose={isProcessing ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle fontWeight={700}>Download Invoices (ZIP)</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Select a date range to download a ZIP file containing PDF copies of all invoices issued in that period.
        </Typography>

        {error && (
          <MuiAlert severity="error" sx={{ mb: 2 }}>
            {error}
          </MuiAlert>
        )}

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box display="flex" flexDirection="column" gap={2}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              disabled={isProcessing}
              format="DD/MM/YYYY"
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              disabled={isProcessing}
              format="DD/MM/YYYY"
            />
          </Box>
        </LocalizationProvider>

        {isProcessing && (
          <Box mt={4} textAlign="center">
            <Typography variant="body2" fontWeight={600} mb={1}>
              Preparing PDFs... {progress}%
            </Typography>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onClose} disabled={isProcessing} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleDownload}
          disabled={isProcessing}
          variant="contained"
          disableElevation
        >
          Download Area
        </Button>
      </DialogActions>
    </Dialog>
  );
}
